import { useEffect, useState, useCallback, useRef, Fragment } from "react";
import { api } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { useAuth } from "../context/AuthContext";
import { CheckCircle, Clock, UploadSimple, X, Eye, ArrowCounterClockwise, Camera, FolderOpen, ChatCircleDots, ThumbsUp, ThumbsDown, Scales, HourglassHigh, CurrencyDollar, MagicWand, Files, Receipt, CaretDown, CaretLeft, CaretRight, FileText, FolderSimplePlus, Question, Prohibit, Plus, PencilSimple, Calendar, Warning, FilePdf } from "@phosphor-icons/react";
import { toast } from "sonner";
import ShareWithBookkeeper from "../components/ShareWithBookkeeper";
import DocumentViewer from "../components/DocumentViewer";
import FlowStrip from "../components/FlowStrip";
import PageHelpButton from "../components/PageHelpButton";
import OwnerBadge, { ownerLeftStripe } from "../components/OwnerBadge";
import OwnerFilter from "../components/OwnerFilter";
import DirectedToPicker from "../components/DirectedToPicker";
import { nextActorForMonthlyItem, ownBucketFor, isBankStatementItem } from "../lib/ownership";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";

function currentMonth() {
  return new Date().toISOString().slice(0, 7);
}

function monthOptions(past = 9, future = 6) {
  const opts = [];
  const now = new Date();
  // Future months (nearest first at top: current, then +1, +2, ...? Actually show upcoming at top)
  for (let i = future; i >= 1; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
    opts.push({ key, label: `${label} (upcoming)` });
  }
  // Current + past months
  for (let i = 0; i < past; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
    opts.push({ key, label });
  }
  return opts;
}

// Parse "2026-04" and return an offset-applied "YYYY-MM" key (delta in months).
// Used by the prev/next arrows on the big month banner.
function shiftMonth(key, delta) {
  const [y, m] = key.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

// "2026-04" → "April 2026"
function monthLabel(key) {
  const [y, m] = key.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

// "2026-04" → "Apr"
function monthLabelShort(key) {
  const [y, m] = key.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-US", { month: "short" });
}

// Classify a YYYY-MM relative to today: "today" / "past" / "upcoming".
function monthStatus(key) {
  const today = currentMonth();
  if (key === today) return "today";
  return key < today ? "past" : "upcoming";
}

// Module-level static — hoisted out of the component body so a new array
// isn't allocated on every render (prevents an unnecessary FlowStrip re-render).
const RECURRING_FLOW_STEPS = [
  { icon: FolderOpen,   title: "Receive doc",    sub: "Email, mail, or download from a vendor portal.", accent: "#002FA7" },
  { icon: UploadSimple, title: "Upload here",    sub: "Drag, click Upload, or use Smart upload.",        accent: "#7C3AED" },
  { icon: MagicWand,    title: "Auto-matched",   sub: "I find the right monthly item for you.",          accent: "#0EA5E9" },
  { icon: CheckCircle,  title: "Submitted ✓",    sub: "Row turns green. Done for the month.",            accent: "#10B981" },
];

export default function Recurring() {
  const { activeCompanyId, activeCompany } = useCompany();
  const { user } = useAuth();
  const [month, setMonth] = useState(currentMonth());
  const [items, setItems] = useState([]);
  const [uploadingId, setUploadingId] = useState(null);
  // Sticky banner: when a bank-statement upload auto-clears N remittances, show
  // a dismissible green pill so the bookkeeper sees the impact of her upload.
  const [autoClearBanner, setAutoClearBanner] = useState(null);
  // Inline template expansion: when set to a templateId, render a 12-month
  // timeline panel directly below the row for that template. Click the
  // template name on a row to toggle. Statement rows only.
  const [expandedTemplateId, setExpandedTemplateId] = useState(null);
  const fileRefs = useRef({});
  const cameraRefs = useRef({});
  const [askOpen, setAskOpen] = useState(null); // item or null
  const [askTitle, setAskTitle] = useState("");
  const [askBody, setAskBody] = useState("");
  // Smart default: opposite of viewer's role; staff defaults to bookkeeper
  const defaultDirectedTo =
    user?.role === "owner" ? "bookkeeper" :
    user?.role === "bookkeeper" ? "owner" :
    "bookkeeper";
  const [askDirectedTo, setAskDirectedTo] = useState(defaultDirectedTo);

  // "Add monthly item" — owner/staff can add new recurring templates inline.
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [newItemName, setNewItemName] = useState("");
  const [newItemVendor, setNewItemVendor] = useState("");
  const [newItemCategory, setNewItemCategory] = useState("statement");
  const [addingItem, setAddingItem] = useState(false);
  const submitNewItem = async () => {
    if (!newItemName.trim()) {
      toast.error("Name is required");
      return;
    }
    setAddingItem(true);
    try {
      await api.post("/recurring-templates", {
        company_id: activeCompanyId,
        name: newItemName.trim(),
        vendor: newItemVendor.trim() || null,
        category: newItemCategory,
        description: null,
      });
      toast.success(`Added "${newItemName.trim()}"`);
      setAddItemOpen(false);
      setNewItemName("");
      setNewItemVendor("");
      setNewItemCategory("statement");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add item");
    }
    setAddingItem(false);
  };

  // Edit / retire existing template
  const [editItem, setEditItem] = useState(null); // monthly item or null
  const [editName, setEditName] = useState("");
  const [editVendor, setEditVendor] = useState("");
  const [editCategory, setEditCategory] = useState("statement");
  const [savingEdit, setSavingEdit] = useState(false);
  const openEdit = (item) => {
    setEditItem(item);
    setEditName(item.name || "");
    setEditVendor(item.vendor || "");
    setEditCategory(item.category || "statement");
  };
  const submitEdit = async () => {
    if (!editItem || !editName.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!editItem.template_id) {
      toast.error("This item isn't tied to a recurring template — can't edit.");
      return;
    }
    setSavingEdit(true);
    try {
      await api.patch(`/recurring-templates/${editItem.template_id}`, {
        name: editName.trim(),
        vendor: editVendor.trim() || null,
        category: editCategory,
      });
      toast.success("Saved");
      setEditItem(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
    setSavingEdit(false);
  };
  const submitRetire = async () => {
    if (!editItem?.template_id) return;
    if (!confirm(`Retire "${editItem.name}"? It will no longer appear on future months. Submitted history is preserved.`)) return;
    setSavingEdit(true);
    try {
      await api.delete(`/recurring-templates/${editItem.template_id}`);
      toast.success(`Retired "${editItem.name}"`);
      setEditItem(null);
      await Promise.all([load(), loadRetiredTemplates()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to retire");
    }
    setSavingEdit(false);
  };
  const [reqOpen, setReqOpen] = useState(null); // item being prepared for approval
  const [reqAmount, setReqAmount] = useState("");
  const [reqNote, setReqNote] = useState("");
  const [reqSubmitting, setReqSubmitting] = useState(false);
  const [smartUploading, setSmartUploading] = useState(false);
  const [smartResults, setSmartResults] = useState(null); // {matched:[], unmatched:[], pending:[...]}
  const smartFileRef = useRef(null);
  const [payOpen, setPayOpen] = useState(null); // item for which we're logging payment
  const [payText, setPayText] = useState("");
  const [payFile, setPayFile] = useState(null); // optional receipt upload (PDF/image)
  const [paySubmitting, setPaySubmitting] = useState(false);
  const [viewConfirmation, setViewConfirmation] = useState(null); // item
  const [viewingDoc, setViewingDoc] = useState(null); // {id, content_type, display_filename, ...}
  const [retiredTemplates, setRetiredTemplates] = useState([]);
  const [retiredOpen, setRetiredOpen] = useState(false);
  const [unretiringId, setUnretiringId] = useState(null);

  const load = useCallback(async () => {
    if (!activeCompanyId) return;
    const { data } = await api.get(`/monthly-items?company_id=${activeCompanyId}&month=${month}`);
    // Hide bank-statement rows — they're handled on the Remittances page.
    // Templates still exist in the DB so the auto-clear pipeline keeps working.
    setItems((data || []).filter((i) => !isBankStatementItem(i)));
  }, [activeCompanyId, month]);

  const loadRetiredTemplates = useCallback(async () => {
    if (!activeCompanyId) return;
    try {
      const { data } = await api.get(`/recurring-templates?company_id=${activeCompanyId}`);
      setRetiredTemplates((data || []).filter((t) => t.active === false));
    } catch {
      // Non-fatal — section just stays hidden
      setRetiredTemplates([]);
    }
  }, [activeCompanyId]);

  const handleUnretire = useCallback(async (template) => {
    if (!template?.id) return;
    if (!window.confirm(`Un-retire "${template.name}"? It will start appearing on the current month again.`)) return;
    setUnretiringId(template.id);
    try {
      await api.post(`/recurring-templates/${template.id}/un-retire`);
      toast.success(`${template.name} is back`);
      await Promise.all([load(), loadRetiredTemplates()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to un-retire");
    }
    setUnretiringId(null);
  }, [load, loadRetiredTemplates]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadRetiredTemplates(); }, [loadRetiredTemplates]);

  const handleFile = async (item, fileList) => {
    const file = fileList?.[0];
    if (!file) return;
    setUploadingId(item.id);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("company_id", activeCompanyId);
    fd.append("category", "statement");
    fd.append("monthly_item_id", item.id);
    try {
      const { data } = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded for ${item.name}`);
      if (data?.auto_clear?.cleared_count > 0) {
        setAutoClearBanner({
          count: data.auto_clear.cleared_count,
          total: data.auto_clear.total_cleared_amount,
          source: item.name,
        });
      }
      await load();
    } catch (e) {
      toast.error("Upload failed");
    }
    setUploadingId(null);
  };

  const handleReset = async (item) => {
    let warning;
    if (item.status === "submitted" && item.decision === "pay" && item.payment_confirmation) {
      warning = `⚠️ Reset "${item.name}" to pending?\n\nThis will erase:\n  • The Pay Now approval\n  • The tax amount (${item.tax_amount ? '$' + Number(item.tax_amount).toFixed(2) : ''})\n  • The payment confirmation text\n\nThis cannot be undone. The bookkeeper would need to redo the entire workflow.`;
    } else if (item.status === "submitted" && item.decision) {
      warning = `Reset "${item.name}" to pending?\n\nThis erases the ${item.decision === 'pay' ? 'Pay Now' : 'Do Not Pay'} approval${item.tax_amount ? ' and the calculated tax amount ($' + Number(item.tax_amount).toFixed(2) + ')' : ''}. The bookkeeper will need to request approval again.`;
    } else if (item.status === "awaiting_approval") {
      warning = `Cancel the bookkeeper's pending approval request for "${item.name}"?\n\nThe tax amount${item.tax_amount ? ' ($' + Number(item.tax_amount).toFixed(2) + ')' : ''} will be cleared. They'll need to request approval again.`;
    } else if (item.status === "submitted" && item.document) {
      warning = `Reset "${item.name}" to pending?\n\nThe attached file will be unlinked from this item but stays in your archive — you can find it under Files in the sidebar and re-attach it any time.`;
    } else {
      warning = `Reset "${item.name}" to pending?`;
    }
    if (!window.confirm(warning)) return;
    try {
      await api.post(`/monthly-items/${item.id}/reset`);
      toast.success("Reset to pending");
      await load();
    } catch (e) {
      toast.error("Failed to reset");
    }
  };

  const handleView = (item) => {
    if (!item.document_id) return;
    setViewingDoc({
      id: item.document_id,
      content_type: item.document?.content_type,
      display_filename: item.document?.display_filename || item.document?.original_filename,
      original_filename: item.document?.original_filename,
    });
  };

  const openAsk = (item) => {
    // Subject: prefer the actual document filename so Tessina (and the bookkeeper) can
    // see *exactly* which document the inquiry is about. Falls back to the item name.
    const docName = item.document?.display_filename || item.document?.original_filename;
    setAskTitle(docName ? `Re: ${docName}` : `Re: ${item.name}`);
    setAskBody("");
    setAskDirectedTo(defaultDirectedTo);
    setAskOpen(item);
  };

  const submitAsk = async () => {
    if (!askTitle.trim() || !askOpen) return;
    try {
      const docName = askOpen.document?.display_filename || askOpen.document?.original_filename;
      const ctxLine = docName
        ? `Regarding "${askOpen.name}" — ${docName} (${month})`
        : `Regarding "${askOpen.name}" (${month})`;
      await api.post("/questions", {
        company_id: activeCompanyId,
        title: askTitle.trim(),
        body: askBody.trim() ? `${ctxLine}:\n${askBody.trim()}` : ctxLine,
        due_date: null,
        linked_document_id: askOpen.document_id || null,
        directed_to: askDirectedTo || null,
      });
      toast.success("Inquiry submitted");
      setAskOpen(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit inquiry");
    }
  };

  const handleDecide = async (item, decision) => {
    try {
      await api.post(`/monthly-items/${item.id}/decide`, { decision });
      toast.success(decision === "pay" ? "Approved — Pay Now" : "Marked — Do Not Pay");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save decision");
    }
  };

  const openRequest = (item) => {
    setReqAmount("");
    setReqNote("");
    setReqOpen(item);
  };

  const submitRequest = async () => {
    if (!reqOpen) return;
    const amt = parseFloat(reqAmount);
    if (!reqAmount || isNaN(amt) || amt < 0) {
      toast.error("Enter a valid tax amount");
      return;
    }
    setReqSubmitting(true);
    try {
      await api.post(`/monthly-items/${reqOpen.id}/request-approval`, {
        tax_amount: amt,
        note: reqNote.trim() || null,
      });
      toast.success("Approval requested from owner");
      setReqOpen(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to request approval");
    }
    setReqSubmitting(false);
  };

  const formatMoney = (n) => {
    if (n === null || n === undefined) return "";
    try {
      return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
    } catch (e) {
      return `$${n}`;
    }
  };

  const decisionLabel = (d) => {
    if (d === "pay") return "PAY NOW";
    if (d === "do_not_pay") return "DO NOT PAY";
    // legacy fallbacks
    if (d === "yes") return "PAY";
    if (d === "no") return "SKIP";
    if (d === "skip") return "SKIP";
    return (d || "").toUpperCase();
  };

  const handleSmartFiles = async (fileList) => {
    if (!fileList || fileList.length === 0 || !activeCompanyId) return;
    const files = Array.from(fileList);
    setSmartUploading(true);
    const results = [];
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("company_id", activeCompanyId);
      fd.append("month", month);
      try {
        const { data } = await api.post("/monthly-items/smart-upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        results.push({ filename: file.name, ...data });
      } catch (e) {
        results.push({ filename: file.name, error: e?.response?.data?.detail || "Upload failed" });
      }
    }
    setSmartUploading(false);
    await load();
    const matched = results.filter((r) => r.matched).length;
    const errors = results.filter((r) => r.error).length;
    const unmatched = results.filter((r) => !r.error && !r.matched && !(r.auto_clear_only && r.auto_clear?.cleared_count > 0)).length;
    const periodCorrected = results.filter((r) => r.period_corrected).length;
    // Only open the results dialog when something actually needs attention:
    // an upload errored, the AI couldn't match a file to a monthly item, or it
    // had to auto-route to a different month than the one you were viewing.
    // Clean successes (every file matched cleanly) get a toast and skip the
    // dialog so you don't have to click Done every time.
    const needsAttention = errors > 0 || unmatched > 0 || periodCorrected > 0;
    if (needsAttention) {
      setSmartResults({ files: results });
    } else if (matched > 0) {
      toast.success(`${matched} ${matched === 1 ? "statement" : "statements"} matched ✓`);
    }
    // Aggregate auto-clear summary across all uploads in this batch.
    const totalCleared = results.reduce((sum, r) => sum + (r.auto_clear?.cleared_count || 0), 0);
    const totalAmount = results.reduce((sum, r) => sum + (r.auto_clear?.total_cleared_amount || 0), 0);
    if (totalCleared > 0) {
      setAutoClearBanner({
        count: totalCleared,
        total: totalAmount,
        source: results.length === 1 ? (results[0].monthly_item_name || results[0].filename) : `${results.length} statements`,
      });
    }
    // Period auto-correction is surfaced inline in the SmartResults dialog
    // (blue "Auto-routed to <month>" badge per file) since the global toast
    // silencer suppresses .info()/.success() — and this is critical audit info.
    if (smartFileRef.current) smartFileRef.current.value = "";
  };

  const handleConfirmPeriod = async (file, targetMonth) => {
    try {
      const { data } = await api.post(`/monthly-items/attach-document`, {
        document_id: file.document_id,
        target_month: targetMonth,
        vendor: file.detected_vendor || null,
      });
      // Replace the entry in smartResults with the resolved one
      setSmartResults((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          files: prev.files.map((f) =>
            f.document_id === file.document_id
              ? {
                  ...f,
                  confirmation_required: false,
                  matched: !!data.matched,
                  monthly_item_id: data.monthly_item_id || null,
                  monthly_item_name: data.monthly_item_name || null,
                  monthly_item_month: data.monthly_item_month,
                  candidates: data.candidates || [],
                }
              : f
          ),
        };
      });
      await load();
      if (data?.auto_clear?.cleared_count > 0) {
        setAutoClearBanner({
          count: data.auto_clear.cleared_count,
          total: data.auto_clear.total_cleared_amount,
          source: data.monthly_item_name || file.filename,
        });
      }
      toast.success(data.matched ? `Filed under ${data.monthly_item_name}` : "Uploaded — pick the item manually");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't attach this");
    }
  };

  const handleManualAttach = async (documentId, itemId) => {
    try {
      const { data } = await api.post(`/monthly-items/${itemId}/attach-document`, { document_id: documentId });
      toast.success("Attached");
      if (data?.auto_clear?.cleared_count > 0) {
        const matched = items.find((i) => i.id === itemId);
        setAutoClearBanner({
          count: data.auto_clear.cleared_count,
          total: data.auto_clear.total_cleared_amount,
          source: matched?.name || "bank statement",
        });
      }
      setSmartResults((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          files: prev.files.map((f) =>
            f.document_id === documentId ? { ...f, matched: true, monthly_item_id: itemId } : f
          ),
        };
      });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to attach");
    }
  };

  const openLogPayment = (item) => {
    setPayText(item.payment_confirmation || "");
    setPayFile(null);
    setPayOpen(item);
  };

  const submitLogPayment = async () => {
    if (!payOpen) return;
    const text = payText.trim();
    if (!text && !payFile) {
      toast.error("Paste confirmation text or upload a receipt");
      return;
    }
    setPaySubmitting(true);
    try {
      let documentId = null;
      // Upload the receipt file first (if any) and get back a document_id.
      if (payFile) {
        const fd = new FormData();
        fd.append("file", payFile);
        fd.append("company_id", activeCompanyId);
        fd.append("category", "other");
        const { data: doc } = await api.post("/documents/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        documentId = doc?.id;
      }
      await api.post(`/monthly-items/${payOpen.id}/log-payment`, {
        confirmation_text: text || null,
        document_id: documentId,
      });
      toast.success("Confirmation saved");
      setPayOpen(null);
      setPayText("");
      setPayFile(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save confirmation");
    }
    setPaySubmitting(false);
  };

  // Mark a Do-Not-Pay item as actually paid (out-of-band). Anyone with
  // owner or staff role can do this — common case is the owner paid the
  // tax himself and wants to record it, or Tessina handled DOR for him.
  const handleFlipToPay = async (item) => {
    const note = prompt(
      `Mark this as paid?\n\nThis will record the tax as PAID and open a dialog where you can attach the receipt.\n\n(Optional) Add a note for the record:`,
      "",
    );
    if (note === null) return; // cancel
    try {
      await api.post(`/monthly-items/${item.id}/change-decision`, {
        decision: "pay",
        note: note?.trim() || null,
      });
      toast.success("Marked as paid");
      await load();
      const refreshed = { ...item, decision: "pay" };
      openLogPayment(refreshed);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    }
  };

  const submittedCount = items.filter((i) => i.status === "submitted").length;
  const awaitingCount = items.filter((i) => i.status === "awaiting_approval").length;
  const pendingCount = items.length - submittedCount - awaitingCount;

  // Group items: tax decisions first (separate, more prominent flow), then regular statements/documents.
  // Within each group, preserve the original ordering coming from the API.
  const decisionItems = items.filter((i) => i.category === "decision");
  const statementItems = items.filter((i) => i.category !== "decision");

  // Ownership filter (page-local, never persisted — defaults to "all" each time)
  const [ownerFilter, setOwnerFilter] = useState("all");
  // A submitted statement (non-decision item with an attached document) is also relevant
  // to the bookkeeper — she needs to review uploaded statements and may want to ask questions.
  // We include those in the bookkeeper bucket in addition to items still actively owned by her.
  const isUploadedStatementForBookkeeper = (i) =>
    i.category !== "decision" && i.status === "submitted" && i.document_id;
  const ownCounts = items.reduce(
    (acc, i) => {
      const a = nextActorForMonthlyItem(i);
      acc.all += 1;
      if (a === "staff") acc.staff += 1;
      else if (a === "bookkeeper") acc.bookkeeper += 1;
      else if (a === "owner") acc.owner += 1;
      else acc.done += 1;
      // Bookkeeper "review queue" — uploaded statements she should be able to see/ask about
      if (a !== "bookkeeper" && isUploadedStatementForBookkeeper(i)) acc.bookkeeper += 1;
      return acc;
    },
    { all: 0, staff: 0, bookkeeper: 0, owner: 0, done: 0 }
  );
  const filterRow = (item) => {
    if (ownerFilter === "all") return true;
    const a = nextActorForMonthlyItem(item);
    if (ownerFilter === "done") return a === null;
    if (ownerFilter === "bookkeeper") {
      return a === "bookkeeper" || isUploadedStatementForBookkeeper(item);
    }
    return a === ownerFilter;
  };
  const visibleDecisions = decisionItems.filter(filterRow);
  const visibleStatements = statementItems.filter(filterRow);
  const visibleAll = [...visibleDecisions, ...visibleStatements];

  return (
    <div data-testid="recurring-page" className="p-6 sm:p-8 max-w-[1400px] pb-24 sm:pb-8">
      {autoClearBanner && (
        <div
          className="mb-4 flex items-start gap-3 bg-[#ECFDF5] border-l-4 border-[#10B981] px-4 py-3"
          data-testid="auto-clear-banner"
        >
          <CheckCircle size={22} weight="fill" className="text-[#10B981] flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="font-heading text-[15px] font-semibold text-[#065F46]">
              {autoClearBanner.count} remittance{autoClearBanner.count !== 1 ? "s" : ""} auto-cleared
              {autoClearBanner.total > 0 ? ` (${autoClearBanner.total.toLocaleString("en-US", { style: "currency", currency: "USD" })})` : ""}
            </div>
            <div className="text-[12px] text-[#065F46] mt-0.5">
              Matched against your "{autoClearBanner.source}" bank statement upload. View them on the Remittances page.
            </div>
          </div>
          <button
            onClick={() => setAutoClearBanner(null)}
            className="text-[#065F46] hover:text-[#10B981] flex-shrink-0 p-1 -m-1"
            aria-label="Dismiss"
            data-testid="auto-clear-banner-dismiss"
          >
            <X size={18} weight="bold" />
          </button>
        </div>
      )}
      {/* Big month banner — the most prominent thing on the page. Replaces
          the tiny dropdown so the user always knows WHICH month they're
          working on. Click the month name to change, or use the arrows. */}
      <div
        className="mb-6 flex items-center justify-between gap-2 sm:gap-4 border-2 border-[#002FA7] bg-[#002FA7]/[0.04] px-3 sm:px-5 py-3 sm:py-4"
        data-testid="month-banner"
      >
        <button
          onClick={() => setMonth(shiftMonth(month, -1))}
          className="flex items-center gap-1.5 px-2.5 sm:px-3 py-2 hover:bg-[#002FA7] hover:text-white transition-colors text-[#002FA7] focus:outline-none focus:bg-[#002FA7] focus:text-white"
          title={`Previous month (${monthLabel(shiftMonth(month, -1))})`}
          data-testid="month-prev"
        >
          <CaretLeft size={18} weight="bold" />
          <span className="text-xs sm:text-sm font-semibold hidden sm:inline tracking-wider uppercase">
            {monthLabelShort(shiftMonth(month, -1))}
          </span>
        </button>

        <div className="flex flex-col items-center gap-1 flex-1 min-w-0">
          <div className="label-tag text-[10px] text-[#525252]">You are working on</div>
          <div className="relative inline-flex items-center">
            <select
              data-testid="month-selector"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              aria-label="Change month"
              className="font-heading text-2xl sm:text-4xl font-bold tracking-tight text-[#002FA7] bg-transparent border-0 focus:outline-none cursor-pointer text-center appearance-none pr-7 sm:pr-9"
            >
              {monthOptions().map((o) => (
                <option key={o.key} value={o.key}>{o.label}</option>
              ))}
            </select>
            <CaretDown
              size={20}
              weight="bold"
              className="absolute right-0 text-[#002FA7] pointer-events-none"
              aria-hidden="true"
            />
          </div>
          {(() => {
            const s = monthStatus(month);
            if (s === "today") {
              return (
                <span className="inline-flex items-center gap-1.5 text-[10px] bg-[#10B981] text-white px-2 py-0.5 font-bold tracking-wider uppercase" data-testid="month-badge-today">
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> This month
                </span>
              );
            }
            if (s === "past") {
              return (
                <span className="inline-flex items-center gap-1.5 text-[10px] bg-[#525252] text-white px-2 py-0.5 font-bold tracking-wider uppercase" data-testid="month-badge-past">
                  Past period
                </span>
              );
            }
            return (
              <span className="inline-flex items-center gap-1.5 text-[10px] bg-[#F59E0B] text-white px-2 py-0.5 font-bold tracking-wider uppercase" data-testid="month-badge-upcoming">
                Upcoming
              </span>
            );
          })()}
        </div>

        <button
          onClick={() => setMonth(shiftMonth(month, 1))}
          className="flex items-center gap-1.5 px-2.5 sm:px-3 py-2 hover:bg-[#002FA7] hover:text-white transition-colors text-[#002FA7] focus:outline-none focus:bg-[#002FA7] focus:text-white"
          title={`Next month (${monthLabel(shiftMonth(month, 1))})`}
          data-testid="month-next"
        >
          <span className="text-xs sm:text-sm font-semibold hidden sm:inline tracking-wider uppercase">
            {monthLabelShort(shiftMonth(month, 1))}
          </span>
          <CaretRight size={18} weight="bold" />
        </button>
      </div>

      <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="label-tag mb-2 flex items-center gap-2">
            <span>Monthly recurring items</span>
            <PageHelpButton
              testId="recurring-help"
              title="How Monthly Items work"
              bullets={[
                <><b>Each row is a recurring item</b> the bookkeeper needs from you this month — Amex statement, vehicle loan statement, tax decisions, etc.</>,
                <>Hit <b>Upload</b> on a row, or use <b>Smart upload</b> (top-right) to drop a stack at once — the AI matches each file to the right row.</>,
                <><b>Tax decisions</b> are color-coded — <span className="inline-block w-2 h-2 rounded-full bg-[#F59E0B] align-middle mx-1" /> yellow = bookkeeper preparing · <span className="inline-block w-2 h-2 rounded-full bg-[#DC2626] align-middle mx-1" /> red = waiting on you · <span className="inline-block w-2 h-2 rounded-full bg-[#10B981] align-middle mx-1" /> green = approved/paid · <span className="inline-block w-2 h-2 rounded-full bg-[#525252] align-middle mx-1" /> gray = do not pay.</>,
                <><b>Bank statements live on the Remittances page</b> — they don&apos;t show up here.</>,
              ]}
              footer={<>The summary cards above always reflect the selected month. Owners can edit templates in Settings → Recurring templates.</>}
            />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight leading-none">
            Outstanding for <span className="text-[#002FA7]">{monthLabel(month)}</span>.
          </h1>
          <p className="text-[#525252] mt-3 max-w-xl">
            {activeCompany?.name}. Generated from recurring templates — uploaded documentation automatically marks an item as submitted.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="pt-0">
            <input
              ref={smartFileRef}
              type="file"
              accept="application/pdf,image/*"
              multiple
              className="hidden"
              onChange={(e) => handleSmartFiles(e.target.files)}
              data-testid="smart-upload-input"
            />
            <button
              onClick={() => smartFileRef.current?.click()}
              disabled={smartUploading}
              className="btn-ikb px-3 py-2 text-sm flex items-center gap-2 disabled:opacity-60"
              title="Upload PDF/image statements — I'll auto-match each to its recurring item"
              data-testid="smart-upload-btn"
            >
              <MagicWand size={16} weight="bold" />
              {smartUploading ? "Matching…" : "Smart upload"}
            </button>
          </div>
          {(user?.role === "owner" || user?.role === "staff") && (
            <div className="pt-0">
              <button
                onClick={() => setAddItemOpen(true)}
                className="px-3 py-2 text-sm border border-[#E5E5E5] hover:border-[#002FA7] hover:text-[#002FA7] flex items-center gap-2 transition-colors"
                title="Add a new recurring monthly item (e.g. a new credit card statement)"
                data-testid="add-monthly-item-btn"
              >
                <Plus size={16} weight="bold" />
                Add monthly item
              </button>
            </div>
          )}
          {(user?.role === "owner" || user?.role === "staff") && retiredTemplates.length > 0 && (
            <div className="pt-0">
              <button
                onClick={() => setRetiredOpen((v) => !v)}
                className="px-3 py-2 text-sm border border-[#F59E0B]/40 bg-[#F59E0B]/[0.05] text-[#92400E] hover:bg-[#F59E0B]/[0.12] hover:border-[#F59E0B] flex items-center gap-2 transition-colors w-full justify-between"
                title="Show templates you retired so you can put them back"
                data-testid="retired-templates-toggle"
              >
                <span className="flex items-center gap-2">
                  <ArrowCounterClockwise size={16} weight="bold" />
                  {retiredTemplates.length} retired template{retiredTemplates.length === 1 ? "" : "s"}
                </span>
                <CaretDown
                  size={14}
                  weight="bold"
                  className={`transition-transform ${retiredOpen ? "rotate-180" : ""}`}
                />
              </button>
              {retiredOpen && (
                <div
                  className="mt-2 border border-[#F59E0B]/30 bg-[#FFFBEB] divide-y divide-[#F59E0B]/20"
                  data-testid="retired-templates-list"
                >
                  {retiredTemplates.map((t) => (
                    <div
                      key={t.id}
                      className="px-3 py-2 flex items-center justify-between gap-2"
                      data-testid={`retired-template-${t.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-[#171717] truncate">{t.name}</div>
                        {t.vendor && <div className="text-[11px] text-[#737373] truncate">{t.vendor}</div>}
                        {t.retired_at && (
                          <div className="text-[10px] text-[#92400E]/70 mt-0.5">
                            Retired {new Date(t.retired_at).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => handleUnretire(t)}
                        disabled={unretiringId === t.id}
                        className="px-2 py-1 text-[11px] font-semibold border border-[#002FA7] bg-[#002FA7] text-white hover:bg-[#001E73] disabled:opacity-50 flex items-center gap-1 flex-shrink-0"
                        data-testid={`retired-template-unretire-${t.id}`}
                      >
                        <ArrowCounterClockwise size={11} weight="bold" />
                        {unretiringId === t.id ? "…" : "Un-retire"}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="pt-5">
            <ShareWithBookkeeper variant="compact" />
          </div>
        </div>
      </div>

      {/* Visual flow guide — appears at the top so it's the first thing she sees */}
      <FlowStrip
        storageKey="flow-recurring-v1"
        title="Monthly checklist — at a glance"
        testId="recurring-flow"
        steps={RECURRING_FLOW_STEPS}
        footer={<>Tax decisions (DOR / L&amp;I) are a separate flow — see the &ldquo;Tax decisions&rdquo; section below.</>}
      />

      {/* How this works — moved into PageHelpButton next to the page title above */}

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 mb-8 surface-card bg-white">
        <div className="p-5 border-r border-[#E5E5E5]">
          <div className="label-tag mb-1">Total</div>
          <div className="font-heading text-3xl font-medium">{items.length}</div>
        </div>
        <div className="p-5 sm:border-r border-[#E5E5E5]">
          <div className="label-tag mb-1">Submitted</div>
          <div className="font-heading text-3xl font-medium text-[#10B981]">{submittedCount}</div>
        </div>
        <div className="p-5 border-r border-t sm:border-t-0 border-[#E5E5E5]">
          <div className="label-tag mb-1">Awaiting owner</div>
          <div className="font-heading text-3xl font-medium text-[#002FA7]">{awaitingCount}</div>
        </div>
        <div className="p-5 border-t sm:border-t-0 border-[#E5E5E5]">
          <div className="label-tag mb-1">Pending</div>
          <div className="font-heading text-3xl font-medium text-[#F59E0B]">{pendingCount}</div>
        </div>
      </div>

      {/* Ownership filter — defaults to All every visit */}
      <div className="mb-4" data-testid="recurring-owner-filter">
        <OwnerFilter
          value={ownerFilter}
          onChange={setOwnerFilter}
          counts={ownCounts}
          viewerRole={user?.role}
          showDone
          testid="recurring-owner-filter"
        />
      </div>

      {/* Checklist */}
      <div className="surface-card bg-white">
        <div className="hidden md:grid grid-cols-12 px-5 py-3 border-b border-[#E5E5E5] bg-[#FAFAFA]">
          <div className="col-span-6 label-tag">Item</div>
          <div className="col-span-2 label-tag">Vendor</div>
          <div className="col-span-2 label-tag">Status</div>
          <div className="col-span-2 label-tag text-right">Actions</div>
        </div>
        {items.length === 0 && (
          <div className="p-10 text-center text-[#525252]">No recurring items configured yet. Add them in Settings.</div>
        )}
        {items.length > 0 && visibleAll.length === 0 && (
          <div className="p-10 text-center text-[#525252] text-sm">
            Nothing matches that filter. <button onClick={() => setOwnerFilter("all")} className="underline ml-1" data-testid="recurring-empty-show-all">Show all</button>
          </div>
        )}
        {visibleAll.map((item, idx, arr) => {
          const prev = idx === 0 ? null : arr[idx - 1];
          const isFirstDecision = item.category === "decision" && (!prev || prev.category !== "decision");
          const isFirstStatement = item.category !== "decision" && (idx === 0 || (prev && prev.category === "decision"));
          const actor = nextActorForMonthlyItem(item);
          const viewerOwns = actor !== null && actor === ownBucketFor(user?.role);
          return (
            <Fragment key={item.id}>
              {isFirstDecision && (
                <div
                  data-testid="recurring-section-tax"
                  className="px-5 py-3 bg-[#002FA7]/[0.06] border-b border-[#002FA7]/30 flex items-center gap-2"
                >
                  <Scales size={16} weight="duotone" className="text-[#002FA7]" />
                  <div className="font-heading font-semibold text-[13px] text-[#002FA7] uppercase tracking-wider">
                    Tax decisions
                  </div>
                  <span className="text-[11px] text-[#525252]">
                    🟡 not prepared · 🔴 okay to pay? · 🟢 approved
                  </span>
                </div>
              )}
              {isFirstStatement && (
                <div
                  data-testid="recurring-section-statements"
                  className="px-5 py-3 bg-[#FAFAFA] border-b border-[#E5E5E5] flex items-center gap-2"
                >
                  <FileText size={16} weight="duotone" className="text-[#525252]" />
                  <div className="font-heading font-semibold text-[13px] text-[#0A0A0A] uppercase tracking-wider">
                    Statements &amp; documents
                  </div>
                  <span className="text-[11px] text-[#525252]">
                    upload to mark submitted
                  </span>
                </div>
              )}
          <div
            data-testid={`recurring-item-${item.id}`}
            style={ownerLeftStripe(actor)}
            className={`grid grid-cols-12 px-5 py-4 border-b border-[#E5E5E5] last:border-b-0 items-center gap-3 transition-all ${
              item.status === "submitted" ? "bg-[#10B981]/[0.02]" : ""
            }`}
          >
            <div className="col-span-12 md:col-span-6 flex items-center gap-3">
              {item.status === "submitted" && item.category === "decision" && item.decision === "do_not_pay" ? (
                <Prohibit size={22} weight="duotone" className="text-[#525252] flex-shrink-0" />
              ) : item.status === "submitted" ? (
                <CheckCircle size={22} weight="fill" className="text-[#10B981] flex-shrink-0" />
              ) : item.status === "awaiting_approval" ? (
                <Question size={22} weight="fill" className="text-[#DC2626] flex-shrink-0" />
              ) : item.category === "decision" ? (
                <Clock size={22} weight="duotone" className="text-[#F59E0B] flex-shrink-0" />
              ) : (
                <Clock size={22} weight="duotone" className="text-[#F59E0B] flex-shrink-0" />
              )}
              <div className="min-w-0">
                <div className={`font-medium flex items-center flex-wrap gap-x-1 gap-y-0.5 ${item.status === "submitted" && item.category !== "decision" ? "line-through text-[#525252]" : ""}`}>
                  {item.template_id && item.category !== "decision" ? (
                    <>
                      {/* Desktop: clickable expand button */}
                      <button
                        onClick={() => setExpandedTemplateId(
                          expandedTemplateId === item.template_id ? null : item.template_id,
                        )}
                        className="hidden md:inline-flex text-left hover:text-[#002FA7] hover:underline transition-colors items-center gap-1"
                        title="See every statement uploaded for this item across the year"
                        data-testid={`expand-template-${item.id}`}
                      >
                        {expandedTemplateId === item.template_id
                          ? <CaretDown size={11} weight="bold" />
                          : <CaretRight size={11} weight="bold" />}
                        <span>{item.name}</span>
                      </button>
                      {/* Mobile: plain name (mobile is for quick uploads only) */}
                      <span className="md:hidden">{item.name}</span>
                    </>
                  ) : (
                    <span>{item.name}</span>
                  )}
                  {item.template_id && (user?.role === "owner" || user?.role === "staff") && (
                    <button
                      onClick={(ev) => { ev.stopPropagation(); openEdit(item); }}
                      className="ml-1 p-1 text-[#A3A3A3] hover:text-[#002FA7] hover:bg-[#F5F5F5] no-underline transition-colors"
                      title="Rename / change category / retire"
                      data-testid={`edit-template-${item.id}`}
                    >
                      <PencilSimple size={13} weight="duotone" />
                    </button>
                  )}
                  {actor && (
                    <span className="ml-2 inline-block align-middle">
                      <OwnerBadge actor={actor} viewerIsOwn={viewerOwns} compact />
                    </span>
                  )}
                  {item.category === "decision" && item.status === "pending" && (
                    <span className="ml-2 inline-flex items-center gap-1 text-[10px] bg-[#F59E0B]/10 text-[#92400E] px-2 py-0.5 font-semibold uppercase tracking-wider">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B]" /> Not prepared
                    </span>
                  )}
                  {item.category === "decision" && item.status === "awaiting_approval" && (
                    <span className="ml-2 inline-flex items-center gap-1 text-[10px] bg-[#DC2626]/10 text-[#991B1B] px-2 py-0.5 font-semibold uppercase tracking-wider">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#DC2626]" /> Okay to pay?
                    </span>
                  )}
                  {item.category === "decision" && item.status === "submitted" && item.decision === "pay" && (
                    (item.payment_confirmation || item.payment_confirmation_document_id) ? (
                      <span className="ml-2 inline-flex items-center gap-1.5 text-xs bg-[#10B981] text-white px-2.5 py-1 font-bold uppercase tracking-wider shadow-sm" data-testid={`decision-paid-${item.id}`}>
                        <CheckCircle size={14} weight="fill" className="text-white" /> Paid
                      </span>
                    ) : (
                      <span className="ml-2 inline-flex items-center gap-1.5 text-xs bg-[#10B981] text-white px-2.5 py-1 font-bold uppercase tracking-wider shadow-sm" data-testid={`decision-approved-pay-${item.id}`}>
                        <CheckCircle size={14} weight="fill" className="text-white" /> Approved — pay
                      </span>
                    )
                  )}
                  {item.category === "decision" && item.status === "submitted" && item.decision === "do_not_pay" && (
                    <span className="ml-2 inline-flex items-center gap-1.5 text-xs bg-[#525252] text-white px-2.5 py-1 font-bold uppercase tracking-wider shadow-sm" data-testid={`decision-do-not-pay-${item.id}`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-white" /> Do not pay
                    </span>
                  )}
                  {item.status === "awaiting_approval" && item.category !== "decision" && (
                    <span className="ml-2 label-tag text-[10px] bg-[#002FA7]/10 text-[#002FA7] px-1.5 py-0.5">Awaiting owner</span>
                  )}
                </div>
                {item.document?.display_filename && (
                  <div className="text-[11px] text-[#525252] truncate font-mono">
                    {item.document.display_filename}
                  </div>
                )}
                {item.status === "awaiting_approval" && item.tax_amount !== undefined && item.tax_amount !== null && (
                  <div className="mt-2 p-2.5 bg-[#FEE2E2] border-l-2 border-[#DC2626]">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="font-heading text-[15px] font-semibold text-[#991B1B]">
                        Okay to pay {formatMoney(item.tax_amount)}?
                      </span>
                      {item.requested_by && (
                        <span className="text-[11px] text-[#7F1D1D]">
                          — prepared by {item.requested_by}
                          {item.requested_at && ` · ${new Date(item.requested_at).toLocaleDateString()}`}
                        </span>
                      )}
                    </div>
                    {item.tax_note && (
                      <div className="text-[11px] text-[#7F1D1D] italic mt-1">{item.tax_note}</div>
                    )}
                  </div>
                )}
                {item.status === "submitted" && item.decision && (
                  <div className="label-tag text-[10px] mt-1 text-[#002FA7]">
                    {item.decision === "pay" && (item.payment_confirmation || item.payment_confirmation_document_id) ? (
                      <>
                        Paid
                        {item.tax_amount !== undefined && item.tax_amount !== null ? ` · ${formatMoney(item.tax_amount)}` : ""}
                        {item.payment_confirmation_at ? ` · on ${new Date(item.payment_confirmation_at).toLocaleDateString()}` : ""}
                      </>
                    ) : (
                      <>
                        Decision: {decisionLabel(item.decision)}
                        {item.tax_amount !== undefined && item.tax_amount !== null ? ` · ${formatMoney(item.tax_amount)}` : ""}
                        {item.decision_note ? ` — ${item.decision_note}` : ""}
                      </>
                    )}
                  </div>
                )}
                {item.status === "submitted" && item.decision === "pay" && (
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    {item.payment_confirmation || item.payment_confirmation_document_id ? (
                      <>
                        <button
                          onClick={() => setViewConfirmation(item)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#10B981] text-white text-xs font-semibold hover:bg-[#059669] transition-colors shadow-sm"
                          data-testid={`view-payment-${item.id}`}
                        >
                          <Eye size={14} weight="duotone" /> View payment receipt
                        </button>
                        {(user?.role === "bookkeeper" || user?.role === "owner" || user?.role === "staff") && (
                          <button
                            onClick={() => openLogPayment(item)}
                            className="text-xs text-[#525252] hover:text-[#0A0A0A] underline"
                            data-testid={`edit-payment-${item.id}`}
                          >
                            edit
                          </button>
                        )}
                      </>
                    ) : (user?.role === "bookkeeper" || user?.role === "owner" || user?.role === "staff") ? (
                      <button
                        onClick={() => openLogPayment(item)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border-2 border-[#10B981] text-[#10B981] text-xs font-semibold hover:bg-[#10B981]/10 transition-colors"
                        title="Paste the portal confirmation or upload a PDF/photo of the receipt."
                        data-testid={`log-payment-${item.id}`}
                      >
                        <Receipt size={14} weight="duotone" /> Add payment receipt
                      </button>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] text-[#F59E0B] italic">
                        <Clock size={11} weight="duotone" /> Awaiting payment confirmation
                      </span>
                    )}
                  </div>
                )}
                {/* Owner or staff: reverse a "Do Not Pay" decision when they ended up paying. */}
                {item.status === "submitted" && item.decision === "do_not_pay" && (user?.role === "owner" || user?.role === "staff") && (
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => handleFlipToPay(item)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 border-2 border-[#002FA7] text-[#002FA7] text-xs font-semibold hover:bg-[#002FA7]/10 transition-colors"
                      title="Did you end up paying after all? Mark this paid and attach a receipt."
                      data-testid={`flip-to-pay-${item.id}`}
                    >
                      <ArrowCounterClockwise size={14} weight="duotone" /> Actually paid this →
                    </button>
                  </div>
                )}
                {item.status === "submitted" && item.submitted_by && (
                  <div className="label-tag text-[10px] mt-1 text-[#10B981]">
                    ✓ Submitted by {item.submitted_by}
                    {item.submitted_at && ` · ${new Date(item.submitted_at).toLocaleDateString()}`}
                  </div>
                )}
              </div>
            </div>
            <div className="col-span-6 md:col-span-2 text-sm text-[#525252]">
              <span className="md:hidden label-tag mr-1">Vendor · </span>{item.vendor || "—"}
            </div>
            <div className="col-span-6 md:col-span-2">
              {/* Tax decisions get traffic-light pills with explicit copy */}
              {item.category === "decision" && item.status === "pending" ? (
                <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-[#F59E0B]/10 text-[#92400E] text-[11px] font-semibold tracking-wider uppercase">
                  <span className="status-dot bg-[#F59E0B]" /> Not prepared
                </span>
              ) : item.category === "decision" && item.status === "awaiting_approval" ? (
                <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-[#DC2626]/10 text-[#991B1B] text-[11px] font-semibold tracking-wider uppercase">
                  <span className="status-dot bg-[#DC2626]" /> Okay to pay?
                </span>
              ) : item.category === "decision" && item.status === "submitted" && item.decision === "do_not_pay" ? (
                <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-[#525252]/10 text-[#525252] text-[11px] font-semibold tracking-wider uppercase">
                  <span className="status-dot bg-[#525252]" /> Do not pay
                </span>
              ) : item.status === "submitted" ? (
                <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-[#10B981]/10 text-[#10B981] text-[11px] font-semibold tracking-wider uppercase">
                  <span className="status-dot bg-[#10B981]" /> {item.category === "decision" ? "Approved" : "Submitted"}
                </span>
              ) : item.status === "awaiting_approval" ? (
                <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-[#002FA7]/10 text-[#002FA7] text-[11px] font-semibold tracking-wider uppercase">
                  <span className="status-dot bg-[#002FA7]" /> Awaiting owner
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-[#F59E0B]/10 text-[#F59E0B] text-[11px] font-semibold tracking-wider uppercase">
                  <span className="status-dot bg-[#F59E0B]" /> Pending
                </span>
              )}
            </div>
            <div className="col-span-12 md:col-span-2 flex flex-col gap-1.5 pt-2 md:pt-0 border-t md:border-t-0 border-[#E5E5E5] md:items-stretch md:max-w-[140px] md:ml-auto">
              {item.status === "submitted" ? (
                <>
                  {item.document_id && (
                    <button
                      onClick={() => handleView(item)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#002FA7]/10 text-[#002FA7] hover:bg-[#002FA7] hover:text-white text-[11px] font-semibold tracking-wider uppercase border border-[#002FA7]/30 transition-colors w-full justify-center"
                      title="View file"
                      data-testid={`view-${item.id}`}
                    >
                      <span className="status-dot bg-current" /> View
                    </button>
                  )}
                  {item.document_id && item.category !== "decision" && (
                    <button
                      onClick={() => openAsk(item)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#002FA7]/10 text-[#002FA7] hover:bg-[#002FA7] hover:text-white text-[11px] font-semibold tracking-wider uppercase border border-[#002FA7]/30 transition-colors w-full justify-center"
                      title="Ask a question about this document"
                      data-testid={`ask-${item.id}`}
                    >
                      <span className="status-dot bg-current" /> Ask
                    </button>
                  )}
                  <button
                    onClick={() => handleReset(item)}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#525252]/10 text-[#525252] hover:bg-[#525252] hover:text-white text-[11px] font-semibold tracking-wider uppercase border border-[#525252]/30 transition-colors w-full justify-center"
                    title="Reset"
                    data-testid={`reset-${item.id}`}
                  >
                    <span className="status-dot bg-current" /> Reset
                  </button>
                </>
              ) : item.category === "decision" ? (
                item.status === "awaiting_approval" ? (
                  user?.role === "owner" ? (
                    <>
                      <button
                        onClick={() => handleDecide(item, "pay")}
                        className="px-3 py-2.5 bg-[#10B981] text-white hover:bg-[#059669] transition-all flex items-center justify-center gap-1.5 w-full text-sm font-semibold"
                        data-testid={`decide-pay-${item.id}`}
                      >
                        <ThumbsUp size={16} weight="bold" /> Yes, pay
                      </button>
                      <button
                        onClick={() => handleDecide(item, "do_not_pay")}
                        className="px-3 py-2.5 border-2 border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626] hover:text-white transition-all flex items-center justify-center gap-1.5 w-full text-sm font-semibold"
                        data-testid={`decide-do-not-pay-${item.id}`}
                      >
                        <ThumbsDown size={16} weight="bold" /> No, don't pay
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="text-[11px] text-[#991B1B] italic flex items-center gap-1.5 md:text-right justify-end w-full">
                        <Question size={14} weight="fill" className="text-[#DC2626]" /> Owner deciding
                      </div>
                      <button
                        onClick={() => handleReset(item)}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#525252]/10 text-[#525252] hover:bg-[#525252] hover:text-white text-[11px] font-semibold tracking-wider uppercase border border-[#525252]/30 transition-colors w-full justify-center"
                        title="Revise amount"
                        data-testid={`reset-${item.id}`}
                      >
                        <span className="status-dot bg-current" /> Revise
                      </button>
                    </>
                  )
                ) : user?.role === "bookkeeper" ? (
                  <button
                    onClick={() => openRequest(item)}
                    className="px-3 py-2.5 bg-[#F59E0B] text-white hover:bg-[#D97706] transition-all flex items-center justify-center gap-1.5 w-full text-sm font-semibold"
                    title="Enter the calculated tax amount and ask the owner Yes/No"
                    data-testid={`request-approval-${item.id}`}
                  >
                    <CurrencyDollar size={16} weight="bold" /> Prepare & ask owner
                  </button>
                ) : (
                  <div className="text-[11px] text-[#92400E] italic flex items-center gap-1.5 md:text-right justify-end w-full">
                    <Clock size={14} weight="duotone" className="text-[#F59E0B]" /> Bookkeeper preparing
                  </div>
                )
              ) : (
                <>
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    ref={(el) => (cameraRefs.current[item.id] = el)}
                    className="hidden"
                    onChange={(e) => handleFile(item, e.target.files)}
                    data-testid={`camera-input-${item.id}`}
                  />
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    ref={(el) => (fileRefs.current[item.id] = el)}
                    className="hidden"
                    onChange={(e) => handleFile(item, e.target.files)}
                    data-testid={`file-input-${item.id}`}
                  />
                  {/* Anyone can ask about a pending statement — bookkeeper especially uses this */}
                  <button
                    onClick={() => openAsk(item)}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#002FA7]/10 text-[#002FA7] hover:bg-[#002FA7] hover:text-white text-[11px] font-semibold tracking-wider uppercase border border-[#002FA7]/30 transition-colors w-full justify-center"
                    title="Ask about this item"
                    data-testid={`ask-${item.id}`}
                  >
                    <span className="status-dot bg-current" /> Ask
                  </button>
                  {/* Mobile: camera primary */}
                  <button
                    onClick={() => cameraRefs.current[item.id]?.click()}
                    disabled={uploadingId === item.id}
                    className="btn-ikb md:hidden px-3 py-2.5 text-sm flex items-center justify-center gap-1.5 w-full"
                    data-testid={`upload-camera-${item.id}`}
                  >
                    <Camera size={16} weight="bold" />
                    {uploadingId === item.id ? "Uploading…" : "Take photo"}
                  </button>
                  <button
                    onClick={() => fileRefs.current[item.id]?.click()}
                    disabled={uploadingId === item.id}
                    className="md:hidden px-3 py-2.5 border border-[#E5E5E5] hover:bg-[#F5F5F5] transition-all text-sm flex items-center justify-center gap-1 w-full"
                    title="Upload file from library"
                    data-testid={`upload-library-${item.id}`}
                  >
                    <FolderOpen size={16} weight="duotone" /> Library
                  </button>
                  {/* Desktop: file picker */}
                  <button
                    onClick={() => fileRefs.current[item.id]?.click()}
                    disabled={uploadingId === item.id}
                    className="btn-ikb hidden md:flex px-3 py-2.5 text-sm items-center justify-center gap-1.5 w-full"
                    title="Upload the PDF / image for this item. It auto-marks the item complete."
                    data-testid={`upload-${item.id}`}
                  >
                    <UploadSimple size={16} weight="bold" />
                    {uploadingId === item.id ? "Uploading…" : "Upload"}
                  </button>
                </>
              )}
            </div>
          </div>
          {/* Inline template timeline — 12-month grid for this template */}
          {item.template_id && item.category !== "decision" && expandedTemplateId === item.template_id && (
            <TemplateTimeline
              templateId={item.template_id}
              companyId={activeCompanyId}
              templateName={item.name}
              onClose={() => setExpandedTemplateId(null)}
              onChanged={load}
            />
          )}
            </Fragment>
          );
        })}
      </div>

      <Dialog open={!!smartResults} onOpenChange={(v) => { if (!v) setSmartResults(null); }}>
        <DialogContent className="rounded-none max-w-2xl" data-testid="smart-results-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <MagicWand size={20} weight="duotone" className="text-[#002FA7]" /> Smart upload results
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 pt-2 max-h-[60vh] overflow-y-auto">
            {(smartResults?.files || []).map((f, idx) => (
              <div
                key={f.document_id || `${f.filename}-${idx}`}
                data-testid={`smart-result-${idx}`}
                className={`p-3 border ${f.matched || (f.auto_clear_only && (f.auto_clear?.cleared_count || 0) > 0) ? "border-[#10B981]/30 bg-[#10B981]/[0.04]" : f.auto_clear_only ? "border-[#737373]/30 bg-[#F5F5F5]" : f.error ? "border-[#DC2626]/30 bg-[#DC2626]/[0.04]" : "border-[#F59E0B]/30 bg-[#F59E0B]/[0.04]"}`}
              >
                <div className="flex items-start gap-2">
                  <Files size={18} weight="duotone" className="mt-0.5 flex-shrink-0 text-[#525252]" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{f.filename}</div>
                    {f.period_corrected && f.monthly_item_month && (
                      <div
                        className="mt-1 inline-flex items-center gap-1.5 px-2 py-0.5 bg-[#002FA7]/10 text-[#002FA7] text-[11px] font-semibold border border-[#002FA7]/30"
                        data-testid={`smart-result-period-corrected-${idx}`}
                        title={`You were viewing ${f.viewed_month}. The AI detected this statement covers ${f.monthly_item_month}, so I submitted it there instead.`}
                      >
                        <Calendar size={11} weight="duotone" />
                        Auto-routed to {(() => {
                          try {
                            const [y, mm] = f.monthly_item_month.split("-").map(Number);
                            return new Date(y, mm - 1, 1).toLocaleString("en-US", { month: "long", year: "numeric" });
                          } catch { return f.monthly_item_month; }
                        })()}
                        {" "}(you were on {(() => {
                          try {
                            const [y, mm] = f.viewed_month.split("-").map(Number);
                            return new Date(y, mm - 1, 1).toLocaleString("en-US", { month: "long" });
                          } catch { return f.viewed_month; }
                        })()})
                      </div>
                    )}
                    {f.matched ? (
                      <div className="text-[12px] text-[#10B981] mt-0.5 flex items-center gap-1">
                        <CheckCircle size={12} weight="fill" /> Matched to <b className="ml-1">{f.monthly_item_name}</b>
                      </div>
                    ) : f.auto_clear_only ? (
                      <div className="mt-0.5 space-y-1" data-testid={`smart-result-auto-clear-only-${idx}`}>
                        <div className="text-[12px] text-[#525252] flex items-start gap-1">
                          <CheckCircle size={12} weight="duotone" className="mt-0.5 flex-shrink-0 text-[#737373]" />
                          <span>
                            {(() => {
                              try {
                                const [y, m] = f.monthly_item_month.split("-").map(Number);
                                const label = new Date(y, m - 1, 1).toLocaleString("en-US", { month: "long", year: "numeric" });
                                return <>The bank-statement checklist for <b>{label}</b> is already complete — saved this file for your records.</>;
                              } catch { return <>Checklist already complete — saved this file for your records.</>; }
                            })()}
                          </span>
                        </div>
                        {(f.auto_clear?.cleared_count || 0) > 0 ? (
                          <div className="text-[12px] text-[#10B981] flex items-center gap-1 font-semibold" data-testid={`smart-result-auto-cleared-${idx}`}>
                            <CheckCircle size={12} weight="fill" />
                            Auto-cleared {f.auto_clear.cleared_count} remittance{f.auto_clear.cleared_count === 1 ? "" : "s"} (${Number(f.auto_clear.total_cleared_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                          </div>
                        ) : (f.auto_clear?.deposits_found || 0) > 0 ? (
                          <div className="text-[12px] text-[#737373]">
                            Found {f.auto_clear.deposits_found} deposit{f.auto_clear.deposits_found === 1 ? "" : "s"} on the statement, but none matched an open remittance.
                          </div>
                        ) : (
                          <div className="text-[12px] text-[#737373]">
                            No deposits could be extracted from this statement.
                          </div>
                        )}
                      </div>
                    ) : f.error ? (
                      <div className="text-[12px] text-[#DC2626] mt-0.5">{f.error}</div>
                    ) : f.confirmation_required ? (
                      <>
                        <div className="text-[12px] text-[#92400E] mt-0.5 flex items-center gap-1">
                          <Calendar size={12} weight="duotone" />
                          AI thinks this is a {(() => {
                            try {
                              const [y, m] = f.detected_month.split("-").map(Number);
                              return new Date(y, m - 1, 1).toLocaleString("en-US", { month: "long", year: "numeric" });
                            } catch { return f.detected_month; }
                          })()} statement (you&apos;re viewing {(() => {
                            try {
                              const [y, m] = f.viewed_month.split("-").map(Number);
                              return new Date(y, m - 1, 1).toLocaleString("en-US", { month: "long" });
                            } catch { return f.viewed_month; }
                          })()}). Where should I file it?
                        </div>
                        <div className="mt-2 flex gap-2 flex-wrap">
                          <button
                            onClick={() => handleConfirmPeriod(f, f.detected_month)}
                            className="px-2.5 py-1 text-[11px] font-semibold bg-[#002FA7] text-white border border-[#002FA7] hover:bg-[#001E73] flex items-center gap-1"
                            data-testid={`smart-result-confirm-detected-${idx}`}
                          >
                            <CheckCircle size={11} weight="bold" />
                            File under {(() => {
                              try {
                                const [y, m] = f.detected_month.split("-").map(Number);
                                return new Date(y, m - 1, 1).toLocaleString("en-US", { month: "long" });
                              } catch { return f.detected_month; }
                            })()}
                          </button>
                          <button
                            onClick={() => handleConfirmPeriod(f, f.viewed_month)}
                            className="px-2.5 py-1 text-[11px] font-semibold bg-white border border-[#737373] text-[#525252] hover:border-[#171717] hover:text-[#171717] flex items-center gap-1"
                            data-testid={`smart-result-confirm-viewed-${idx}`}
                          >
                            File under {(() => {
                              try {
                                const [y, m] = f.viewed_month.split("-").map(Number);
                                return new Date(y, m - 1, 1).toLocaleString("en-US", { month: "long" });
                              } catch { return f.viewed_month; }
                            })()} instead
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="text-[12px] text-[#F59E0B] mt-0.5">I couldn&apos;t identify this — pick the right item:</div>
                        <div className="mt-2 flex gap-2 items-center">
                          <select
                            className="flex-1 bg-white border border-[#E5E5E5] px-2 py-1.5 text-sm focus:outline-none focus:border-[#002FA7]"
                            data-testid={`manual-match-select-${idx}`}
                            onChange={(e) => {
                              if (e.target.value) handleManualAttach(f.document_id, e.target.value);
                            }}
                            defaultValue=""
                          >
                            <option value="" disabled>Choose item…</option>
                            {(f.candidates || []).map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.name}{c.vendor ? ` — ${c.vendor}` : ""}
                              </option>
                            ))}
                          </select>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <button onClick={() => setSmartResults(null)} className="btn-ikb px-4 py-2" data-testid="smart-results-close">Done</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DocumentViewer
        open={!!viewingDoc}
        onClose={() => setViewingDoc(null)}
        docs={viewingDoc ? [viewingDoc] : []}
        startId={viewingDoc?.id}
      />

      <Dialog open={!!payOpen} onOpenChange={(v) => { if (!v) { setPayOpen(null); setPayText(""); setPayFile(null); } }}>
        <DialogContent className="rounded-none max-w-2xl" data-testid="log-payment-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Receipt size={20} weight="duotone" className="text-[#10B981]" /> Add payment receipt
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="bg-[#10B981]/[0.06] border-l-2 border-[#10B981] p-4 rounded-sm">
              <div className="font-heading font-semibold text-sm mb-1">Two ways to save your receipt</div>
              <ul className="text-sm text-[#333] space-y-1 list-disc list-inside leading-relaxed">
                <li><b>Upload a PDF / photo</b> of the confirmation page (best when you have a download).</li>
                <li><b>Paste the confirmation text</b> from the tax portal (Ctrl+A · Ctrl+C · Ctrl+V).</li>
              </ul>
              <div className="text-xs text-[#525252] mt-2">
                You can do either, or both. Whoever needs to see it later (owner or bookkeeper) will find it on this row.
              </div>
            </div>
            <div className="bg-[#F5F5F5] p-3 border-l-2 border-[#10B981]">
              <div className="label-tag text-[10px] mb-1">Linked item</div>
              <div className="font-heading font-medium">{payOpen?.name}</div>
              <div className="label-tag text-[10px]">
                {month}
                {payOpen?.tax_amount !== undefined && payOpen?.tax_amount !== null ? ` · Approved ${formatMoney(payOpen.tax_amount)}` : ""}
              </div>
            </div>
            <div>
              <label className="label-tag block mb-2">Receipt file (optional — PDF, JPG, PNG)</label>
              {payFile ? (
                <div className="flex items-center justify-between gap-2 px-3 py-2.5 border-2 border-[#10B981] bg-[#10B981]/[0.06]">
                  <div className="flex items-center gap-2 min-w-0">
                    <Receipt size={16} weight="duotone" className="text-[#10B981] flex-shrink-0" />
                    <span className="text-sm font-medium truncate">{payFile.name}</span>
                    <span className="text-xs text-[#525252] flex-shrink-0">
                      ({(payFile.size / 1024).toFixed(1)} KB)
                    </span>
                  </div>
                  <button
                    onClick={() => setPayFile(null)}
                    className="text-xs text-[#525252] hover:text-[#DC2626] flex-shrink-0 underline"
                    data-testid="pay-file-remove"
                  >
                    remove
                  </button>
                </div>
              ) : (
                <label
                  className="flex items-center justify-center gap-2 px-3 py-3 border-2 border-dashed border-[#E5E5E5] hover:border-[#10B981] hover:bg-[#10B981]/[0.04] cursor-pointer transition-colors text-sm text-[#525252]"
                  data-testid="pay-file-picker"
                >
                  <UploadSimple size={16} weight="duotone" />
                  <span>Choose a file or take a photo</span>
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    capture="environment"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) setPayFile(f);
                    }}
                    data-testid="pay-file-input"
                  />
                </label>
              )}
            </div>
            <div>
              <label className="label-tag block mb-2">Confirmation text (optional)</label>
              <textarea
                value={payText}
                onChange={(e) => setPayText(e.target.value)}
                rows={8}
                placeholder={"Paste here (Ctrl+V on Windows · ⌘V on Mac)\n\nExample:\nConfirmation\nWashington State Department of Revenue\nYour Return has been submitted and your confirmation number is 0-052-165-101.\n..."}
                className="w-full px-3 py-2.5 border border-[#E5E5E5] focus:border-[#10B981] focus:outline-none resize-y font-mono text-sm"
                data-testid="pay-confirmation-text"
              />
              <p className="text-[11px] text-[#525252] mt-1">
                Formatting doesn&apos;t matter — paste exactly what&apos;s on the portal.
              </p>
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={() => { setPayOpen(null); setPayText(""); setPayFile(null); }}
              className="px-4 py-2 border border-[#E5E5E5]"
              data-testid="pay-cancel"
            >
              Cancel
            </button>
            <button
              onClick={submitLogPayment}
              disabled={paySubmitting || (!payText.trim() && !payFile)}
              className="btn-ikb px-4 py-2 disabled:opacity-60"
              data-testid="pay-submit"
            >
              {paySubmitting ? "Saving…" : "Save receipt"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewConfirmation} onOpenChange={(v) => { if (!v) setViewConfirmation(null); }}>
        <DialogContent className="rounded-none max-w-2xl" data-testid="view-payment-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Receipt size={20} weight="duotone" className="text-[#10B981]" /> Payment receipt
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <div className="font-heading font-medium">{viewConfirmation?.name}</div>
              <div className="label-tag text-[10px] text-[#525252]">
                {month}
                {viewConfirmation?.tax_amount !== undefined && viewConfirmation?.tax_amount !== null ? ` · ${formatMoney(viewConfirmation.tax_amount)}` : ""}
              </div>
              {viewConfirmation?.payment_confirmation_by && (
                <div className="label-tag text-[10px] mt-1 text-[#10B981]">
                  ✓ Logged by {viewConfirmation.payment_confirmation_by}
                  {viewConfirmation.payment_confirmation_at && ` · ${new Date(viewConfirmation.payment_confirmation_at).toLocaleDateString()}`}
                </div>
              )}
            </div>
            {viewConfirmation?.payment_confirmation_document_id && (
              <button
                onClick={() => setViewingDoc({ id: viewConfirmation.payment_confirmation_document_id })}
                className="inline-flex items-center gap-1.5 px-3 py-2 border-2 border-[#10B981] text-[#10B981] text-sm font-semibold hover:bg-[#10B981]/10 transition-colors"
                data-testid="payment-view-receipt-doc"
              >
                <Eye size={14} weight="duotone" /> Open uploaded receipt
              </button>
            )}
            {viewConfirmation?.payment_confirmation && (
              <pre
                data-testid="payment-confirmation-content"
                className="bg-[#FAFAFA] border border-[#E5E5E5] p-4 text-sm whitespace-pre-wrap font-mono max-h-[50vh] overflow-y-auto"
              >{viewConfirmation.payment_confirmation}</pre>
            )}
          </div>
          <DialogFooter>
            <button
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(viewConfirmation?.payment_confirmation || "");
                  toast.success("Copied to clipboard");
                } catch (e) {
                  toast.error("Copy failed");
                }
              }}
              className="px-4 py-2 border border-[#E5E5E5]"
              data-testid="payment-copy"
            >
              Copy
            </button>
            <button onClick={() => setViewConfirmation(null)} className="btn-ikb px-4 py-2" data-testid="payment-close">
              Close
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reqOpen} onOpenChange={(v) => { if (!v) setReqOpen(null); }}>
        <DialogContent className="rounded-none max-w-lg" data-testid="request-approval-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Request owner approval</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="bg-[#002FA7]/[0.04] border-l-2 border-[#002FA7] rounded-sm p-4">
              <div className="font-heading font-semibold text-sm mb-1">How this works</div>
              <ol className="text-sm text-[#333] space-y-1 list-decimal list-inside leading-relaxed">
                <li>Enter the <b>tax amount you calculated</b> after closing the books.</li>
                <li>Add an optional note (e.g., what period it covers).</li>
                <li>Click <b>Request approval</b> — the owner gets a notification.</li>
                <li>The owner taps <b>Pay Now</b> or <b>Do Not Pay</b>; once paid, come back here and click <b>Paste payment confirmation</b>.</li>
              </ol>
            </div>
            <div className="bg-[#F5F5F5] p-3 border-l-2 border-[#002FA7]">
              <div className="label-tag text-[10px] mb-1">Linked item</div>
              <div className="font-heading font-medium">{reqOpen?.name}</div>
              <div className="label-tag text-[10px]">{month}</div>
            </div>
            <div>
              <label className="label-tag block mb-2">Tax amount (USD) *</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#525252]">$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={reqAmount}
                  onChange={(e) => setReqAmount(e.target.value)}
                  placeholder="0.00"
                  className="w-full pl-7 pr-3 py-2.5 border border-[#E5E5E5] focus:border-[#002FA7] focus:outline-none font-mono"
                  data-testid="req-amount"
                  autoFocus
                />
              </div>
            </div>
            <div>
              <label className="label-tag block mb-2">Note (optional)</label>
              <textarea
                value={reqNote}
                onChange={(e) => setReqNote(e.target.value)}
                rows={3}
                placeholder="e.g. Based on Q1 payroll totals, due by the 25th."
                className="w-full px-3 py-2.5 border border-[#E5E5E5] focus:border-[#002FA7] focus:outline-none resize-none"
                data-testid="req-note"
              />
            </div>
            <p className="text-[11px] text-[#525252]">
              The owner will see this amount and choose <b>Pay Now</b> or <b>Do Not Pay</b>.
            </p>
          </div>
          <DialogFooter>
            <button onClick={() => setReqOpen(null)} className="px-4 py-2 border border-[#E5E5E5]" data-testid="req-cancel">Cancel</button>
            <button onClick={submitRequest} disabled={reqSubmitting} className="btn-ikb px-4 py-2 disabled:opacity-60" data-testid="req-submit">
              {reqSubmitting ? "Sending…" : "Request approval"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!askOpen} onOpenChange={(v) => { if (!v) setAskOpen(null); }}>
        <DialogContent className="rounded-none max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Ask about this item</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="bg-[#F5F5F5] p-3 border-l-2 border-[#002FA7]">
              <div className="label-tag text-[10px] mb-1">Linked item</div>
              <div className="font-heading font-medium">{askOpen?.name}</div>
              {(askOpen?.document?.display_filename || askOpen?.document?.original_filename) && (
                <div className="label-tag text-[10px] text-[#525252] mt-0.5 truncate font-mono">
                  📎 {askOpen?.document?.display_filename || askOpen?.document?.original_filename}
                </div>
              )}
              <div className="label-tag text-[10px]">{month}</div>
            </div>
            <div>
              <label className="label-tag block mb-2">Subject</label>
              <input
                value={askTitle}
                onChange={(e) => setAskTitle(e.target.value)}
                className="w-full px-3 py-2.5 border border-[#E5E5E5] focus:border-[#002FA7] focus:outline-none"
                data-testid="ask-title"
              />
            </div>
            <div>
              <label className="label-tag block mb-2">What do you need?</label>
              <textarea
                value={askBody}
                onChange={(e) => setAskBody(e.target.value)}
                rows={4}
                placeholder="e.g. Please send the PDF for this statement."
                className="w-full px-3 py-2.5 border border-[#E5E5E5] focus:border-[#002FA7] focus:outline-none resize-none"
                data-testid="ask-body"
              />
            </div>
            <div>
              <label className="label-tag block mb-2">Who&rsquo;s this for?</label>
              <DirectedToPicker
                value={askDirectedTo}
                onChange={setAskDirectedTo}
                testid="ask-directed"
              />
              <div className="text-[11px] text-[#525252] mt-1">
                Only that person gets the email/in-app notification.
              </div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setAskOpen(null)} className="px-4 py-2 border border-[#E5E5E5]">Cancel</button>
            <button onClick={submitAsk} className="btn-ikb px-4 py-2" data-testid="ask-submit">Submit inquiry</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add monthly item — owner/staff only */}
      <Dialog open={addItemOpen} onOpenChange={(v) => { if (!v) setAddItemOpen(false); }}>
        <DialogContent className="rounded-none max-w-lg" data-testid="add-item-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Plus size={20} weight="bold" className="text-[#002FA7]" />
              Add a monthly recurring item
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-[#525252]">
              This will appear on every month going forward. Tessina (or whoever's
              uploading) will be prompted to submit a document for it each month.
            </p>
            <div>
              <label className="label-tag block mb-2">Name (what shows in the list)</label>
              <input
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
                placeholder="e.g. Banner Checking"
                className="w-full px-3 py-2.5 border border-[#E5E5E5] focus:border-[#002FA7] focus:outline-none"
                data-testid="add-item-name"
                autoFocus
              />
            </div>
            <div>
              <label className="label-tag block mb-2">Vendor / Bank (optional)</label>
              <input
                value={newItemVendor}
                onChange={(e) => setNewItemVendor(e.target.value)}
                placeholder="e.g. Banner Bank"
                className="w-full px-3 py-2.5 border border-[#E5E5E5] focus:border-[#002FA7] focus:outline-none"
                data-testid="add-item-vendor"
              />
            </div>
            <div>
              <label className="label-tag block mb-2">Category</label>
              <select
                value={newItemCategory}
                onChange={(e) => setNewItemCategory(e.target.value)}
                className="w-full px-3 py-2.5 border border-[#E5E5E5] focus:border-[#002FA7] focus:outline-none bg-white"
                data-testid="add-item-category"
              >
                <option value="statement">Bank or Credit-Card Statement</option>
                <option value="loan">Loan Statement</option>
                <option value="invoice">Recurring Invoice / Bill</option>
                <option value="decision">Tax / Decision</option>
                <option value="other">Other</option>
              </select>
              <div className="text-[11px] text-[#525252] mt-1">
                Picks the right icon and matching rules for uploads.
              </div>
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={() => setAddItemOpen(false)}
              className="px-4 py-2 border border-[#E5E5E5]"
              data-testid="add-item-cancel"
            >
              Cancel
            </button>
            <button
              onClick={submitNewItem}
              disabled={addingItem || !newItemName.trim()}
              className="btn-ikb px-4 py-2 disabled:opacity-60"
              data-testid="add-item-submit"
            >
              {addingItem ? "Adding…" : "Add item"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit / retire monthly item — owner+staff only */}
      <Dialog open={!!editItem} onOpenChange={(v) => { if (!v) setEditItem(null); }}>
        <DialogContent className="rounded-none max-w-lg" data-testid="edit-item-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <PencilSimple size={18} weight="duotone" className="text-[#002FA7]" />
              Edit monthly item
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-xs text-[#525252]">
              Changes apply to this template and all <b>pending</b> months. Already-submitted
              entries keep their original name for audit history.
            </p>
            <div>
              <label className="label-tag block mb-2">Name</label>
              <input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="w-full px-3 py-2.5 border border-[#E5E5E5] focus:border-[#002FA7] focus:outline-none"
                data-testid="edit-item-name"
                autoFocus
              />
            </div>
            <div>
              <label className="label-tag block mb-2">Vendor / Bank</label>
              <input
                value={editVendor}
                onChange={(e) => setEditVendor(e.target.value)}
                className="w-full px-3 py-2.5 border border-[#E5E5E5] focus:border-[#002FA7] focus:outline-none"
                data-testid="edit-item-vendor"
              />
            </div>
            <div>
              <label className="label-tag block mb-2">Category</label>
              <select
                value={editCategory}
                onChange={(e) => setEditCategory(e.target.value)}
                className="w-full px-3 py-2.5 border border-[#E5E5E5] focus:border-[#002FA7] focus:outline-none bg-white"
                data-testid="edit-item-category"
              >
                <option value="statement">Bank or Credit-Card Statement</option>
                <option value="loan">Loan Statement</option>
                <option value="invoice">Recurring Invoice / Bill</option>
                <option value="decision">Tax / Decision</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
          <DialogFooter className="flex-row justify-between sm:justify-between gap-2">
            <button
              onClick={submitRetire}
              disabled={savingEdit}
              className="px-3 py-2 text-sm border border-[#DC2626]/40 bg-[#FEE2E2] text-[#DC2626] hover:bg-[#DC2626] hover:text-white transition-colors disabled:opacity-60"
              title="Retire this item — won't appear on future months. History preserved."
              data-testid="edit-item-retire"
            >
              Retire item
            </button>
            <div className="flex gap-2">
              <button
                onClick={() => setEditItem(null)}
                className="px-4 py-2 border border-[#E5E5E5]"
                data-testid="edit-item-cancel"
              >
                Cancel
              </button>
              <button
                onClick={submitEdit}
                disabled={savingEdit || !editName.trim()}
                className="btn-ikb px-4 py-2 disabled:opacity-60"
                data-testid="edit-item-submit"
              >
                {savingEdit ? "Saving…" : "Save changes"}
              </button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mobile-only sticky upload dock — always within thumb reach, regardless
          of scroll position. Helps any role (owner, bookkeeper, staff) on a phone. */}
      <div
        className="sm:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-[#E5E5E5] shadow-[0_-2px_8px_rgba(0,0,0,0.08)] pb-[env(safe-area-inset-bottom)]"
        data-testid="mobile-upload-dock"
      >
        <div className="flex gap-2 p-3">
          <button
            onClick={() => smartFileRef.current?.click()}
            disabled={smartUploading}
            className="btn-ikb flex-1 px-3 py-4 flex items-center justify-center gap-2 text-base font-semibold disabled:opacity-60"
            data-testid="mobile-dock-smart-upload"
            aria-label="Smart upload statements"
          >
            <MagicWand size={20} weight="bold" />
            <span>{smartUploading ? "Matching…" : "Smart upload"}</span>
          </button>
        </div>
      </div>
    </div>
  );
}


// ----------------------------------------------------------------------
// Inline template timeline (Reoccurring Monthly → click a template name).
// Shows 12 months Jan-Dec of the chosen year for one recurring template.
// Each month: status + attached file preview link + per-month upload.
// Plus a "Batch upload statements" button at the top — drop many PDFs and
// the AI routes each to the right month (uses the existing smart-upload
// endpoint). Statement rows only — tax-decision rows don't get this view.
// ----------------------------------------------------------------------
function TemplateTimeline({ templateId, companyId, templateName, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [uploadingMonth, setUploadingMonth] = useState(null);
  const [batchUploading, setBatchUploading] = useState(false);
  const [batchResults, setBatchResults] = useState(null);
  const [pendingPicker, setPendingPicker] = useState([]); // [{filename, document_id, suggested_month}]
  const monthFilePickerRefs = useRef({});
  const batchFileRef = useRef(null);

  const load = useCallback(async () => {
    if (!templateId || !companyId) return;
    setLoading(true);
    try {
      const { data } = await api.get(
        `/recurring-templates/${templateId}/timeline?company_id=${companyId}&year=${year}`,
      );
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load timeline");
    } finally {
      setLoading(false);
    }
  }, [templateId, companyId, year]);

  useEffect(() => { load(); }, [load]);

  const uploadForMonth = async (month, files) => {
    if (!files?.length) return;
    setUploadingMonth(month);
    try {
      // For per-month uploads we don't trust the AI; we explicitly tell the
      // backend which month this file is for using the attach-document path.
      // Step 1: upload the raw file as a document, Step 2: attach it.
      const fd = new FormData();
      fd.append("file", files[0]);
      fd.append("company_id", companyId);
      fd.append("category", "statement");
      const { data: doc } = await api.post("/documents/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await api.post("/monthly-items/attach-document", {
        document_id: doc.id,
        target_month: month,
        vendor: data?.template?.vendor || null,
        template_id: templateId,
      });
      toast.success(`Uploaded for ${monthLabel(month)}`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingMonth(null);
    }
  };

  const batchUpload = async (files) => {
    if (!files?.length) return;
    setBatchUploading(true);
    const results = [];
    const needsPicker = [];
    // /monthly-items/smart-upload requires a `month` form field. We send the
    // FIRST month of the viewing year as a baseline; the AI then detects the
    // actual statement period and the response tells us the real month. Then
    // we explicitly attach each doc to the right month using template_id so
    // we don't rely on the smart-upload's vendor-matching (which could miss
    // when there's no pending item for that month yet).
    const baselineMonth = `${year}-01`;
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("company_id", companyId);
        fd.append("month", baselineMonth);
        try {
          const { data: r } = await api.post("/monthly-items/smart-upload", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          // The document was saved (smart-upload returns a document_id even
          // when it can't match). Decide which month to bind it to.
          const detectedMonth = r.monthly_item_month || r.detected_month || null;
          if (r.document_id && detectedMonth) {
            try {
              await api.post("/monthly-items/attach-document", {
                document_id: r.document_id,
                target_month: detectedMonth,
                template_id: templateId,
                vendor: data?.template?.vendor || null,
              });
              results.push({ filename: file.name, month: detectedMonth, matched: true });
            } catch (e) {
              // attach-document failed — push to picker so the user can choose manually
              needsPicker.push({
                filename: file.name,
                document_id: r.document_id,
                suggested_month: detectedMonth,
              });
            }
          } else if (r.document_id) {
            // AI couldn't read a period — queue for the manual picker.
            needsPicker.push({
              filename: file.name,
              document_id: r.document_id,
              suggested_month: null,
            });
          } else {
            results.push({ filename: file.name, error: "Couldn't read file" });
          }
        } catch (e) {
          const detail = e?.response?.data?.detail || e?.message || "Upload failed";
          results.push({ filename: file.name, error: detail });
        }
      }
      setBatchResults({ results, needsPicker });
      setPendingPicker(needsPicker);
      const okCount = results.filter((r) => r.matched).length;
      const errCount = results.filter((r) => r.error).length;
      if (okCount > 0) toast.success(`${okCount} ${okCount === 1 ? "statement" : "statements"} attached ✓`);
      if (errCount > 0) toast.error(`${errCount} ${errCount === 1 ? "file" : "files"} failed — check the list below`);
      if (needsPicker.length > 0) toast(`${needsPicker.length} ${needsPicker.length === 1 ? "file needs" : "files need"} a month assignment below`);
      await load();
      onChanged?.();
    } finally {
      setBatchUploading(false);
      if (batchFileRef.current) batchFileRef.current.value = "";
    }
  };

  const assignPicker = async (entry, month) => {
    try {
      await api.post("/monthly-items/attach-document", {
        document_id: entry.document_id,
        target_month: month,
        vendor: data?.template?.vendor || null,
        template_id: templateId,
      });
      setPendingPicker((p) => p.filter((e) => e.document_id !== entry.document_id));
      toast.success(`Assigned to ${monthLabel(month)}`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to assign");
    }
  };

  if (loading && !data) {
    return (
      <div className="px-5 py-4 bg-[#FAFAFA] border-t border-[#E5E5E5] text-sm text-[#525252]" data-testid={`timeline-${templateId}`}>
        Loading timeline…
      </div>
    );
  }
  if (!data) return null;

  return (
    <div
      className="bg-[#FAFAFA] border-t border-[#E5E5E5] px-5 py-5"
      data-testid={`timeline-${templateId}`}
    >
      {/* Header: year picker + batch upload + close */}
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-3">
          <div className="label-tag text-[10px]">
            Every {templateName} for
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setYear((y) => y - 1)}
              className="p-1 border border-[#E5E5E5] hover:border-[#0A0A0A] text-[#525252]"
              aria-label="Previous year"
              data-testid={`timeline-year-prev-${templateId}`}
            >
              <CaretLeft size={12} weight="bold" />
            </button>
            <div className="px-3 py-0.5 border border-[#0A0A0A] bg-white text-sm font-semibold min-w-[68px] text-center">
              {year}
            </div>
            <button
              onClick={() => setYear((y) => y + 1)}
              className="p-1 border border-[#E5E5E5] hover:border-[#0A0A0A] text-[#525252]"
              aria-label="Next year"
              data-testid={`timeline-year-next-${templateId}`}
            >
              <CaretRight size={12} weight="bold" />
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => batchFileRef.current?.click()}
            disabled={batchUploading}
            className="btn-ikb px-3 py-2 text-xs flex items-center gap-1.5 disabled:opacity-60"
            data-testid={`timeline-batch-upload-${templateId}`}
            title="Drop many PDFs at once — the AI routes each to the right month"
          >
            <MagicWand size={14} weight="bold" />
            {batchUploading ? "Uploading…" : "Batch upload statements"}
          </button>
          <input
            ref={batchFileRef}
            type="file"
            multiple
            accept="application/pdf,image/*"
            className="hidden"
            onChange={(e) => batchUpload(e.target.files)}
            data-testid={`timeline-batch-input-${templateId}`}
          />
          <button
            onClick={onClose}
            className="p-1.5 border border-[#E5E5E5] hover:border-[#0A0A0A] text-[#525252]"
            title="Collapse"
            data-testid={`timeline-close-${templateId}`}
          >
            <X size={12} weight="bold" />
          </button>
        </div>
      </div>

      {/* 12-month grid */}
      <div className="bg-white border border-[#E5E5E5] divide-y divide-[#E5E5E5]" data-testid={`timeline-months-${templateId}`}>
        {data.rows.map((row) => {
          const submitted = row.item?.status === "submitted";
          const isUploading = uploadingMonth === row.month;
          return (
            <div
              key={row.month}
              className={`grid grid-cols-12 gap-3 px-4 py-3 items-center ${submitted ? "bg-[#10B981]/[0.04]" : ""}`}
              data-testid={`timeline-row-${row.month}`}
            >
              <div className="col-span-3 sm:col-span-2 flex items-center gap-2">
                {submitted ? (
                  <CheckCircle size={18} weight="fill" className="text-[#10B981] flex-shrink-0" />
                ) : (
                  <Clock size={18} weight="duotone" className="text-[#F59E0B] flex-shrink-0" />
                )}
                <span className="font-medium text-sm">
                  {new Date(`${row.month}-01T00:00:00`).toLocaleDateString("en-US", { month: "short" })}
                </span>
              </div>
              <div className="col-span-5 sm:col-span-6 min-w-0">
                {row.document ? (
                  <button
                    onClick={() => window.open(`/api/documents/${row.document.id}/download`, "_blank")}
                    className="text-left text-sm hover:text-[#002FA7] hover:underline truncate w-full inline-flex items-center gap-1.5"
                    data-testid={`timeline-doc-${row.month}`}
                    title="Open this statement"
                  >
                    <FilePdf size={14} weight="duotone" className="text-[#002FA7] flex-shrink-0" />
                    <span className="truncate">{row.document.display_filename || row.document.original_filename}</span>
                  </button>
                ) : (
                  <span className="text-sm text-[#A3A3A3] italic">No statement uploaded</span>
                )}
                {row.item?.submitted_at && (
                  <div className="text-[11px] text-[#737373] mt-0.5">
                    uploaded {new Date(row.item.submitted_at).toLocaleDateString()}
                  </div>
                )}
              </div>
              <div className="col-span-4 sm:col-span-4 flex justify-end">
                <input
                  ref={(el) => { monthFilePickerRefs.current[row.month] = el; }}
                  type="file"
                  accept="application/pdf,image/*"
                  className="hidden"
                  onChange={(e) => uploadForMonth(row.month, e.target.files)}
                  data-testid={`timeline-upload-input-${row.month}`}
                />
                <button
                  onClick={() => monthFilePickerRefs.current[row.month]?.click()}
                  disabled={isUploading}
                  className={`text-xs px-3 py-1.5 border transition-colors flex items-center gap-1.5 ${
                    submitted
                      ? "border-[#E5E5E5] text-[#525252] hover:border-[#0A0A0A] hover:text-[#0A0A0A]"
                      : "border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white"
                  } disabled:opacity-50`}
                  data-testid={`timeline-upload-btn-${row.month}`}
                  title={submitted ? "Replace the statement for this month" : "Upload the statement for this month"}
                >
                  <UploadSimple size={12} weight="bold" />
                  {isUploading ? "Uploading…" : (submitted ? "Replace" : "Upload")}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Picker for files the AI couldn't auto-route */}
      {pendingPicker.length > 0 && (
        <div className="mt-4 p-3 bg-[#FEF3C7] border border-[#F59E0B]/40" data-testid={`timeline-picker-${templateId}`}>
          <div className="flex items-center gap-2 mb-2">
            <Warning size={16} weight="duotone" className="text-[#92400E]" />
            <span className="text-sm font-semibold text-[#92400E]">
              {pendingPicker.length} {pendingPicker.length === 1 ? "file needs" : "files need"} a month assignment
            </span>
          </div>
          <div className="space-y-2">
            {pendingPicker.map((entry) => (
              <div key={entry.document_id} className="bg-white border border-[#E5E5E5] p-2 flex items-center gap-2 flex-wrap" data-testid={`timeline-picker-row-${entry.document_id}`}>
                <FilePdf size={14} weight="duotone" className="text-[#525252] flex-shrink-0" />
                <span className="text-xs flex-1 truncate min-w-[120px]">{entry.filename}</span>
                <span className="text-[11px] text-[#525252]">Which month?</span>
                <select
                  defaultValue={entry.suggested_month || ""}
                  onChange={(e) => { if (e.target.value) assignPicker(entry, e.target.value); }}
                  className="text-xs px-2 py-1 border border-[#E5E5E5] bg-white"
                  data-testid={`timeline-picker-select-${entry.document_id}`}
                >
                  <option value="" disabled>Pick a month…</option>
                  {data.rows.map((row) => (
                    <option key={row.month} value={row.month}>
                      {new Date(`${row.month}-01T00:00:00`).toLocaleDateString("en-US", { month: "long", year: "numeric" })}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Batch results summary */}
      {batchResults?.results.length > 0 && (
        <div className="mt-3 text-xs text-[#525252]" data-testid={`timeline-batch-summary-${templateId}`}>
          {batchResults.results.filter((r) => r.matched).length > 0 && (
            <span className="text-[#10B981] font-medium">
              ✓ {batchResults.results.filter((r) => r.matched).length} matched
            </span>
          )}
          {batchResults.results.filter((r) => r.error).length > 0 && (
            <span className="text-[#DC2626] ml-3">
              ✗ {batchResults.results.filter((r) => r.error).length} failed
            </span>
          )}
        </div>
      )}
    </div>
  );
}

